import { routes } from '@/utils/axios';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '@/utils/utils';
import { QueryClient } from '@tanstack/query-core';
import axios, { isAxiosError } from 'axios';
import * as jose from 'jose';
import { Mutex } from 'async-mutex';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export const getQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 60000,
        gcTime: 60000,
      },
    },
  });

type TokenInfo = {
  accessToken: string;
  refreshed: boolean;
} & (
  | {
      refreshed: true;
      refreshToken: string;
    }
  | {
      refreshed: false;
      refreshToken?: never;
    }
);

/// Échange le jeton de rafraîchissement contre une paire neuve, et la range
/// aussitôt.
///
/// Le rangement se fait ici, et non chez l'appelant, parce que le serveur fait
/// tourner le jeton à chaque échange : il retient le dernier émis et détruit la
/// session dès qu'il en revoit un périmé, réutilisation qu'il lit comme un vol.
/// Écrire après coup laissait le magasin sur l'ancien jeton pendant le temps
/// d'une microtâche — assez pour que la requête suivante, libérée par le verrou
/// juste avant cette écriture, rejoue le jeton déjà consommé et fasse effacer
/// la session. Deux requêtes partent de front au premier rendu du tableau de
/// bord, et le jeton d'accès ne vit que trente minutes : toute fermeture plus
/// longue que cela rejouait la scène au lancement suivant.
async function refreshToken(): Promise<TokenInfo | null> {
  const token = window.store.get(REFRESH_TOKEN);
  if (!token) return null;

  const tokenData = await axios
    .post(`${API_URL}${routes.auth.token}`, { refreshToken: token })
    .catch((err: unknown) => {
      // Le serveur a répondu, et il refuse : la session est bel et bien finie.
      if (isAxiosError(err) && err.response && err.response.status < 500) {
        return null;
      }
      // Réseau absent ou serveur en panne : les jetons en place valent
      // toujours. Les abandonner déconnecterait sur une avarie passagère —
      // fréquent au lancement, l'application démarrant souvent avant que la
      // machine ait retrouvé son réseau.
      throw err;
    });

  if (!tokenData?.data) return null;
  const { accessToken, refreshToken: rotated } = tokenData.data;
  if (!accessToken || !rotated) return null;

  window.store.set(ACCESS_TOKEN, accessToken);
  window.store.set(REFRESH_TOKEN, rotated);

  return {
    accessToken,
    refreshToken: rotated,
    refreshed: true,
  };
}

const mutex = new Mutex();

/// `rejected` : un jeton que le serveur vient de refuser (401). Il n'est
/// jamais rendu tel quel, même s'il paraît encore valide — l'horloge du poste
/// peut retarder sur celle du serveur. S'il a déjà été remplacé, par une
/// requête voisine, le jeton courant est rendu sans nouvel échange.
export async function getAccessToken(
  options: { rejected?: string } = {},
): Promise<TokenInfo | null> {
  const release = await mutex.acquire();
  try {
    const token = window.store.get(ACCESS_TOKEN);
    if (!token || token === options.rejected) return await refreshToken();
    try {
      const decoded = jose.decodeJwt(token);
      // Un jeton d'accès illisible ne condamne pas la session : le jeton de
      // rafraîchissement, lui, peut très bien tenir encore. On repart de lui
      // plutôt que d'émettre une requête sans en-tête, qui reviendrait en 401
      // et déconnecterait pour de bon.
      if (!decoded || typeof decoded === 'string' || !decoded.exp) {
        return await refreshToken();
      }
      if (decoded.exp < Date.now() / 1000 + 300) {
        return await refreshToken();
      }
    } catch {
      return await refreshToken();
    }
    return { accessToken: token, refreshed: false };
  } finally {
    release();
  }
}
